import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Get the display name for a user, preferring name over alias.
 * Falls back to alias if name is empty or contains an email.
 * For unmigrated users (from Canny import), always shows alias.
 */
export function getUserDisplayName(user: {
  name: string;
  alias?: string | null;
  isMigrated?: boolean;
}): string {
  // Unmigrated users (Canny imports that haven't been claimed) always show alias
  if (user.isMigrated === false) {
    return user.alias || 'Anonymous User';
  }

  // If name is empty or contains @ symbol (likely an email), use alias
  if (!user.name || user.name.includes('@')) {
    return user.alias || user.name;
  }

  // Otherwise, prefer name
  return user.name;
}

/**
 * Get the initial letter for a user's avatar, safely handling emails.
 */
export function getUserInitial(user: {
  name: string;
  alias?: string | null;
  isMigrated?: boolean;
}): string {
  const displayName = getUserDisplayName(user);
  return displayName[0]?.toUpperCase() || '?';
}

/**
 * Get the avatar URL for a user.
 * Returns null for unmigrated users (should show placeholder).
 */
export function getUserAvatarUrl(user: {
  avatarURL?: string | null;
  isMigrated?: boolean;
}): string | null {
  // Unmigrated users always show placeholder (no real avatar)
  if (user.isMigrated === false) {
    return null;
  }
  return user.avatarURL || null;
}
